import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full bg-white border border-[#e5e5e5] text-[#121212] px-4 py-3 text-sm focus:border-[#121212] focus:outline-none rounded-none";
const labelClass = "block text-xs font-mono uppercase tracking-[.14em] text-[#555555] mb-2";

const AdminPodcastEditPage = () => {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "",
    episode_number: "",
    description: "",
    guest: "",
    artwork: "",
    audio_url: "",
    apple_url: "",
    spotify_url: "",
    status: "draft",
    published_at: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isNew) {
      axios.get(`${API}/admin/podcast/${id}`, { withCredentials: true }).then((res) => {
        const ep = res.data;
        const links = ep.platform_links || [];
        const findLink = (name) => links.find((l) => l.name === name)?.url || "";
        setForm({
          title: ep.title || "",
          episode_number: ep.episode_number || "",
          description: ep.description || "",
          guest: ep.guest || "",
          artwork: ep.artwork || "",
          audio_url: ep.audio_url || "",
          apple_url: findLink("Apple Podcasts"),
          spotify_url: findLink("Spotify"),
          status: ep.status || "draft",
          published_at: ep.published_at || "",
        });
      });
    }
  }, [id, isNew]);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  async function save(status) {
    setBusy(true);
    try {
      const platform_links = [
        form.apple_url && { name: "Apple Podcasts", url: form.apple_url },
        form.spotify_url && { name: "Spotify", url: form.spotify_url },
      ].filter(Boolean);
      const payload = {
        title: form.title,
        episode_number: form.episode_number ? Number(form.episode_number) : null,
        description: form.description,
        guest: form.guest,
        artwork: form.artwork,
        audio_url: form.audio_url,
        platform_links,
        status,
        published_at: form.published_at || null,
      };
      if (isNew) {
        await axios.post(`${API}/admin/podcast`, payload, { withCredentials: true });
      } else {
        await axios.put(`${API}/admin/podcast/${id}`, payload, { withCredentials: true });
      }
      toast.success(status === "published" ? "Episode published." : "Draft saved.");
      navigate("/admin/podcast");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save the episode.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="admin-podcast-edit">
      <Link
        to="/admin/podcast"
        className="text-xs font-mono uppercase tracking-[.14em] text-[#555555] hover:text-[#e02020] inline-block mb-6"
      >
        ← Back to Episodes
      </Link>
      <h1 className="font-disp text-3xl text-[#121212] mb-8">
        {isNew ? "New Episode" : "Edit Episode"}
      </h1>

      <div className="bg-white border border-[#e5e5e5] p-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <label htmlFor="ep-title" className={labelClass}>Episode Title</label>
            <input id="ep-title" data-testid="ep-title-input" type="text" required value={form.title} onChange={update("title")} className={inputClass} />
          </div>
          <div>
            <label htmlFor="ep-number" className={labelClass}>Episode #</label>
            <input id="ep-number" data-testid="ep-number-input" type="number" min="1" value={form.episode_number} onChange={update("episode_number")} className={inputClass} />
          </div>
        </div>

        <div>
          <label htmlFor="ep-description" className={labelClass}>Description</label>
          <textarea id="ep-description" data-testid="ep-description-textarea" rows={5} value={form.description} onChange={update("description")} className={inputClass} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="ep-guest" className={labelClass}>Guest (optional)</label>
            <input id="ep-guest" data-testid="ep-guest-input" type="text" value={form.guest} onChange={update("guest")} className={inputClass} />
          </div>
          <div>
            <label htmlFor="ep-published-at" className={labelClass}>Publish Date (optional)</label>
            <input id="ep-published-at" data-testid="ep-published-at-input" type="datetime-local" value={form.published_at ? form.published_at.slice(0, 16) : ""} onChange={update("published_at")} className={inputClass} />
          </div>
        </div>

        <div>
          <label htmlFor="ep-artwork" className={labelClass}>Episode Artwork URL</label>
          <input id="ep-artwork" data-testid="ep-artwork-input" type="url" value={form.artwork} onChange={update("artwork")} placeholder="https://…" className={inputClass} />
        </div>

        <div>
          <label htmlFor="ep-audio" className={labelClass}>Audio / Embed URL (optional)</label>
          <input id="ep-audio" data-testid="ep-audio-input" type="url" value={form.audio_url} onChange={update("audio_url")} placeholder="https://…" className={inputClass} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="ep-apple" className={labelClass}>Apple Podcasts URL (optional)</label>
            <input id="ep-apple" data-testid="ep-apple-input" type="url" value={form.apple_url} onChange={update("apple_url")} placeholder="https://podcasts.apple.com/…" className={inputClass} />
          </div>
          <div>
            <label htmlFor="ep-spotify" className={labelClass}>Spotify URL (optional)</label>
            <input id="ep-spotify" data-testid="ep-spotify-input" type="url" value={form.spotify_url} onChange={update("spotify_url")} placeholder="https://open.spotify.com/…" className={inputClass} />
          </div>
        </div>

        <div className="flex flex-wrap gap-4 pt-4 border-t border-[#e5e5e5]">
          <button
            type="button"
            onClick={() => save("draft")}
            disabled={busy}
            data-testid="ep-save-draft-button"
            className="px-6 py-3 border border-[#121212] text-[#121212] text-xs font-mono uppercase tracking-[.14em] hover:bg-[#121212] hover:text-white transition-colors rounded-none disabled:opacity-60"
          >
            Save Draft
          </button>
          <button
            type="button"
            onClick={() => save("published")}
            disabled={busy}
            data-testid="ep-publish-button"
            className="px-6 py-3 bg-[#e02020] text-white text-xs font-mono uppercase tracking-[.14em] hover:bg-[#121212] transition-colors rounded-none disabled:opacity-60"
          >
            {form.published_at && new Date(form.published_at) > new Date() ? "Schedule" : "Publish"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminPodcastEditPage;
